import { hashWords } from "daedalus-core";

/**
 * Where a shot is drawn and how it varies, from its launch alone (ADR-095 decision 18), so every page draws the same
 * shot. Its ground point runs the straight line from the origin to the landing point by the fraction of the flight
 * covered; a look may lift the sprite over that point by a parabola that peaks half way, and may start it at a height,
 * the hand it leaves, from which it comes down to the ground at the landing. The only variation, the
 * strip's starting frame and the burst's mirroring, comes from the shared seeded hash over the shot's id, its landing
 * point and its ability. Every function here is pure and allocates nothing.
 */

/**
 * A shot's place: its ground x and y in sixteenths of a tile at `POINT_X` and `POINT_Y`, and how far the sprite is
 * lifted above it, in pixels, at `POINT_LIFT`. The three numbers lie in a typed array, not an object's fields, so that
 * writing them every frame boxes none of them.
 */
export type ProjectilePoint = Float64Array;

/**
 * The index of a point's ground x.
 */
export const POINT_X = 0;

/**
 * The index of a point's ground y.
 */
export const POINT_Y = 1;

/**
 * The index of a point's lift above the ground.
 */
export const POINT_LIFT = 2;

/**
 * A point at the origin with no lift, for `projectilePoint` to fill.
 *
 * @returns The point.
 */
export function newProjectilePoint(): ProjectilePoint {
  return new Float64Array(3);
}

const MIRROR_BIT = 16;

/**
 * The words the variation hashes, reused for every shot.
 */
const WORDS = new Uint32Array(3);

/**
 * A shot's place at a fraction of its flight.
 *
 * @param originX Where the flight starts, in sixteenths.
 * @param originY Where the flight starts.
 * @param landingX Where it lands.
 * @param landingY Where it lands.
 * @param fraction The fraction of the line covered, held to 0 to 1.
 * @param arcPixels How far the look lifts the sprite at mid-flight, 0 for a level flight.
 * @param launchPixels How far above its ground point the look's sprite starts (the hand it leaves), from which it
 *   comes down evenly to the ground at the landing; 0 for a shot that starts on the ground.
 * @param out The point to fill.
 * @returns `out`.
 */
export function projectilePoint(originX: number, originY: number, landingX: number, landingY: number, fraction: number, arcPixels: number, launchPixels: number, out: ProjectilePoint): ProjectilePoint {
  const covered = fraction > 0 ? Math.min(fraction, 1) : 0;
  out[POINT_X] = originX + (landingX - originX) * covered;
  out[POINT_Y] = originY + (landingY - originY) * covered;
  out[POINT_LIFT] = (launchPixels + 4 * arcPixels * covered) * (1 - covered);
  return out;
}

/**
 * The angle of a shot's line on the page, which a look that turns to its heading is drawn at.
 *
 * @param originX Where the flight starts.
 * @param originY Where the flight starts.
 * @param landingX Where it lands.
 * @param landingY Where it lands.
 * @returns The angle in radians, 0 to the right and a quarter turn downward; 0 for a shot that lands where it starts.
 */
export function projectileHeading(originX: number, originY: number, landingX: number, landingY: number): number {
  return Math.atan2(landingY - originY, landingX - originX);
}

/**
 * A shot's variation: the shared hash seeded with its id over its landing point and its ability.
 *
 * @param id The shot's id.
 * @param landingX Where it lands, in sixteenths.
 * @param landingY Where it lands.
 * @param ability The ability's number.
 * @returns An unsigned 32-bit value, the same on every page given the same launch.
 */
export function projectileVariation(id: number, landingX: number, landingY: number, ability: number): number {
  WORDS[0] = landingX;
  WORDS[1] = landingY;
  WORDS[2] = ability;
  return hashWords(id >>> 0, WORDS, 3);
}

/**
 * The frame a shot's strip starts at.
 *
 * @param variation The shot's variation.
 * @param frames How many frames the strip has.
 * @returns A frame from 0, below the strip's count; 0 for a strip of one frame or none.
 */
export function projectileStartFrame(variation: number, frames: number): number {
  return frames > 1 ? variation % frames : 0;
}

/**
 * Whether a shot's burst is drawn mirrored.
 *
 * @param variation The shot's variation.
 * @returns True for a mirrored burst.
 */
export function projectileBurstMirrored(variation: number): boolean {
  return ((variation >>> MIRROR_BIT) & 1) === 1;
}

/**
 * The frame of a looping strip shown at a time since its launch.
 *
 * @param startFrame The frame the strip started at.
 * @param elapsedMs The time since the launch.
 * @param frameMs How long a frame shows.
 * @param frames How many frames the strip has.
 * @returns The frame, 0 for a strip with no frames; a strip with no frame time stays on its starting frame.
 */
export function projectileFrame(startFrame: number, elapsedMs: number, frameMs: number, frames: number): number {
  if (frames <= 0) {
    return 0;
  }
  const passed = frameMs > 0 && elapsedMs > 0 ? Math.floor(elapsedMs / frameMs) : 0;
  return (startFrame + passed) % frames;
}
